/**
 * Privacy Policy Page
 *
 * Displays the privacy policy for ClosedLoopDiving services.
 * Covers data collection, Firebase/Stripe handling, and GDPR compliance.
 */

import { Layout } from '@/components/Layout';

// Privacy policy content
const privacyContent = `
# Privacy Policy

**Last Updated: February 3, 2026**

Welcome to ClosedLoopDiving.com ("Website," "we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Website and use our services. Please read this policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Website.

---

## 1. Information We Collect

### 1.1 Personal Information

We may collect personally identifiable information that you voluntarily provide to us when you:

- Create an account or register for our services
- Purchase a course or subscription
- Apply to our Instructor Referral Program
- Contact us with inquiries or feedback
- Subscribe to our newsletter

This information may include:

- **Contact Information**: Name, email address, phone number
- **Account Information**: Username, password (encrypted), profile preferences
- **Payment Information**: Credit card details (processed securely by Stripe)
- **Professional Information**: For instructors - organization name, certifying agency, certifications
- **Communication Data**: Messages, feedback, and support inquiries

### 1.2 Automatically Collected Information

When you access our Website, we automatically collect certain information, including:

- **Device Information**: Browser type, operating system, device identifiers
- **Usage Data**: Pages visited, time spent on pages, navigation paths
- **Location Data**: General geographic location based on IP address
- **Learning Analytics**: Quiz scores, lesson progress, mastery levels

### 1.3 Cookies and Tracking Technologies

We use cookies and similar tracking technologies to track activity on our Website. Cookies are small data files stored on your device. We use:

- **Essential Cookies**: Required for the Website to function properly
- **Analytics Cookies**: Help us understand how visitors use the Website
- **Authentication Cookies**: Keep you signed in to your account

You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.

---

## 2. How We Use Your Information

We use the information we collect to:

- **Provide Services**: Create and manage your account, process payments, deliver courses
- **Personalize Experience**: Customize content based on your preferences and learning progress
- **Improve Our Platform**: Analyze usage patterns to enhance our educational content
- **Communicate**: Send important updates, respond to inquiries, and provide customer support
- **Process Payments**: Handle transactions securely through our payment processor
- **Administer Programs**: Manage the Instructor Referral Program, issue promotional codes
- **Ensure Security**: Protect against fraud, unauthorized access, and other security issues
- **Comply with Law**: Meet legal obligations and respond to lawful requests

---

## 3. Third-Party Services

We use trusted third-party services to help operate our Website:

### 3.1 Firebase (Google)

We use Firebase for:

- **Authentication**: Secure user sign-in and account management
- **Database**: Storing learning progress and user preferences
- **Hosting**: Delivering our Website content
- **Cloud Functions**: Processing backend operations

Firebase's privacy practices are governed by Google's Privacy Policy. Data is stored securely in accordance with Google Cloud's security standards.

### 3.2 Stripe

We use Stripe for payment processing. When you make a purchase:

- Card details are sent directly to Stripe's secure servers
- We do not store full credit card numbers on our servers
- Stripe is PCI-DSS compliant

Stripe's privacy practices are governed by their Privacy Policy at stripe.com/privacy.

### 3.3 Anthropic (Claude AI)

Our AI-powered answer evaluation uses Claude by Anthropic. When you submit answers:

- Your answer text is sent to Anthropic for evaluation
- We do not send personal identification information with answer evaluations
- Anthropic's data handling is governed by their usage policies

### 3.4 Resend (Email)

We use Resend for transactional emails such as:

- Password reset emails
- Verification codes
- Instructor program notifications

---

## 4. Data Retention

We retain your personal information for as long as:

- Your account is active
- We need it to provide services to you
- Required by law or for legitimate business purposes

You may request deletion of your account and associated data at any time (see Section 7).

---

## 5. Data Security

We implement appropriate technical and organizational measures to protect your personal information, including:

- **Encryption**: Data encrypted in transit (HTTPS/TLS) and at rest
- **Access Controls**: Role-based access to sensitive data
- **Security Headers**: CSP, HSTS, and other security headers implemented
- **Regular Audits**: Periodic security reviews and updates

However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.

---

## 6. International Data Transfers

Our services are hosted on Google Cloud infrastructure, which may involve data storage and processing in various locations. By using our services, you consent to the transfer of information to countries outside your country of residence, which may have different data protection rules.

For users in the European Economic Area (EEA), we ensure appropriate safeguards are in place for international transfers.

---

## 7. Your Rights

Depending on your location, you may have the following rights regarding your personal information:

### 7.1 Access and Portability

You have the right to:

- Request a copy of your personal data
- Receive your data in a structured, commonly used format

### 7.2 Correction

You have the right to:

- Update or correct inaccurate personal information
- Complete incomplete information

### 7.3 Deletion

You have the right to:

- Request deletion of your personal data
- Close your account

To delete your account, contact us at privacy@closedloopdiving.com.

### 7.4 Restriction and Objection

You have the right to:

- Restrict processing of your personal data in certain circumstances
- Object to processing based on legitimate interests

### 7.5 Withdraw Consent

Where we rely on consent, you have the right to withdraw consent at any time.

### 7.6 Complaints

You have the right to lodge a complaint with your local data protection authority.

---

## 8. Children's Privacy

Our Website is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you believe we have collected information from a child under 16, please contact us immediately.

---

## 9. Marketing Communications

We may send you marketing communications about our services if you have:

- Created an account and not opted out
- Explicitly consented to receive marketing

You can opt out of marketing communications at any time by:

- Clicking "unsubscribe" in any marketing email
- Updating your preferences in your account settings
- Contacting us at privacy@closedloopdiving.com

---

## 10. Updates to This Policy

We may update this Privacy Policy from time to time. We will notify you of any changes by:

- Posting the new Privacy Policy on this page
- Updating the "Last Updated" date
- Sending you an email (for significant changes)

We encourage you to review this Privacy Policy periodically.

---

## 11. Contact Us

If you have questions about this Privacy Policy or our data practices, please contact us:

**ClosedLoopDiving**
Email: privacy@closedloopdiving.com

For general inquiries: info@closedloopdiving.com

---

## 12. California Privacy Rights

If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):

- Right to know what personal information is collected
- Right to know whether personal information is sold or disclosed
- Right to say no to the sale of personal information
- Right to equal service and price

We do not sell personal information.

---

## 13. Australian Privacy Principles

For Australian residents, we comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988. This includes:

- Open and transparent management of personal information
- Anonymity and pseudonymity options where practicable
- Collection of solicited personal information only when necessary
- Dealing with unsolicited personal information appropriately
- Notification of the collection of personal information
- Use or disclosure of personal information only for intended purposes
- Security of personal information

---

*Your privacy matters to us. Dive safe, and rest assured your data is in good hands.*
`;

export function Privacy() {
  // Simple markdown-to-HTML conversion for the privacy content
  const renderMarkdown = (content: string) => {
    return content
      .split('\n')
      .map((line, index) => {
        // H1
        if (line.startsWith('# ')) {
          return (
            <h1
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-2xl)',
                fontWeight: 'var(--cld-font-weight-bold)',
                marginTop: 'var(--cld-spacing-xl)',
                marginBottom: 'var(--cld-spacing-md)',
              }}
            >
              {line.replace('# ', '')}
            </h1>
          );
        }
        // H2
        if (line.startsWith('## ')) {
          return (
            <h2
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-xl)',
                fontWeight: 'var(--cld-font-weight-semibold)',
                marginTop: 'var(--cld-spacing-lg)',
                marginBottom: 'var(--cld-spacing-sm)',
              }}
            >
              {line.replace('## ', '')}
            </h2>
          );
        }
        // H3
        if (line.startsWith('### ')) {
          return (
            <h3
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-lg)',
                fontWeight: 'var(--cld-font-weight-semibold)',
                marginTop: 'var(--cld-spacing-md)',
                marginBottom: 'var(--cld-spacing-xs)',
              }}
            >
              {line.replace('### ', '')}
            </h3>
          );
        }
        // Horizontal rule
        if (line.trim() === '---') {
          return (
            <hr
              key={index}
              style={{
                border: 'none',
                borderTop: '1px solid var(--cld-color-gray)',
                margin: 'var(--cld-spacing-lg) 0',
              }}
            />
          );
        }
        // List item
        if (line.startsWith('- ')) {
          return (
            <li
              key={index}
              style={{
                color: 'var(--cld-color-gray)',
                marginLeft: 'var(--cld-spacing-md)',
                marginBottom: 'var(--cld-spacing-xs)',
              }}
            >
              {line.replace('- ', '')}
            </li>
          );
        }
        // Bold text handling and regular paragraph
        if (line.trim()) {
          // Replace **text** with bold spans
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <p
              key={index}
              style={{
                color: 'var(--cld-color-gray)',
                lineHeight: 1.7,
                marginBottom: 'var(--cld-spacing-sm)',
              }}
            >
              {parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return (
                    <strong key={i} style={{ color: 'var(--cld-color-primary)' }}>
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return part;
              })}
            </p>
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  return (
    <Layout>
      <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: 'var(--cld-spacing-xl)',
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--cld-color-white)',
            borderRadius: 'var(--cld-radius-lg)',
            padding: 'var(--cld-spacing-xl)',
            boxShadow: 'var(--cld-shadow-md)',
          }}
        >
          {renderMarkdown(privacyContent)}
        </div>
      </div>
    </Layout>
  );
}

export default Privacy;
